import {
  FieldWrapper,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { TWorkflowBaseStepDefinition, WorkflowStepType } from '@commenda/types';
import { TDataSourceCall } from '@commenda/types/src/workflow/dsl/datasource';
import { FieldRefSource } from '@commenda/types/src/workflow/dsl/enums';
import { TFieldRef } from '@commenda/types/src/workflow/dsl/values';

export const FieldRefEditor = ({
  value,
  onChange,
  allowedSources,
  steps = [],
  dataSources = [],
}: {
  value: TFieldRef;
  onChange: (next: TFieldRef) => void;
  allowedSources?: FieldRefSource[];
  steps?: TWorkflowBaseStepDefinition[];
  dataSources?: TDataSourceCall[];
}) => {
  const { source } = value;

  return (
    <div className="flex flex-col gap-3">
      <FieldWrapper>
        <Label>Source</Label>
        <Select
          value={source}
          onValueChange={(s: FieldRefSource) => {
            // Reset structure-specific fields when source changes
            if (s === FieldRefSource.CONTEXT || s === FieldRefSource.EACH)
              onChange({ source: s, path: '' });
            if (s === FieldRefSource.STEP)
              onChange({ source: FieldRefSource.STEP, stepId: '', path: '' });
            if (s === FieldRefSource.DATASOURCE)
              onChange({
                source: FieldRefSource.DATASOURCE,
                key: '',
                path: '',
              });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select source" />
          </SelectTrigger>
          <SelectContent>
            {allowedSources ? (
              allowedSources.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))
            ) : (
              <>
                <SelectItem value={FieldRefSource.CONTEXT}>ctx</SelectItem>
                <SelectItem value={FieldRefSource.STEP}>step</SelectItem>
                <SelectItem value={FieldRefSource.DATASOURCE}>ds</SelectItem>
                <SelectItem value={FieldRefSource.EACH}>each</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </FieldWrapper>

      {source === FieldRefSource.STEP && (
        <FieldWrapper>
          <Label>Step ID</Label>
          <Select
            value={value.stepId}
            onValueChange={(selectedStepId) =>
              onChange({
                source: FieldRefSource.STEP,
                stepId: selectedStepId,
                path: value.path,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select step" />
            </SelectTrigger>
            <SelectContent>
              {steps
                // human.form steps never declare an outputSchema but always
                // produce outputData.formResponse, so they are valid sources.
                ?.filter(
                  (s) =>
                    s.outputSchema || s.type === WorkflowStepType.HUMAN_FORM
                )
                .map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </FieldWrapper>
      )}

      {source === FieldRefSource.DATASOURCE && (
        <FieldWrapper>
          <Label>Data Source ID</Label>
          <Select
            value={value.key}
            onValueChange={(selectedDataSourceId) =>
              onChange({
                source: FieldRefSource.DATASOURCE,
                key: selectedDataSourceId,
                path: value.path,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select data source" />
            </SelectTrigger>
            <SelectContent>
              {dataSources.map((ds) => (
                <SelectItem key={ds.dataSourceID} value={ds.dataSourceID}>
                  {ds.alias ?? ds.dataSourceID}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldWrapper>
      )}

      <FieldWrapper>
        <Label>Path</Label>
        <Input
          placeholder="Only dot notation path (e.g., user.name.first)"
          value={value.path}
          onChange={(e) => {
            if (
              source === FieldRefSource.CONTEXT ||
              source === FieldRefSource.EACH
            )
              onChange({ source, path: e.target.value });
            if (source === FieldRefSource.STEP)
              onChange({
                source: FieldRefSource.STEP,
                stepId: value.stepId || '',
                path: e.target.value,
              });
            if (source === FieldRefSource.DATASOURCE)
              onChange({
                source: FieldRefSource.DATASOURCE,
                key: value.key || '',
                path: e.target.value,
              });
          }}
        />
      </FieldWrapper>
    </div>
  );
};
